import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Link } from "react-router-dom";
import { SETTINGS_CHANGE_PASSWORD } from "@/lib/links";
import { Button } from "./ui/button";
import {
  Card,
  CardTitle,
  CardContent,
  CardFooter,
  CardHeader,
  CardDescription,
} from "./ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from "./ui/form";
import { Input } from "./ui/input";
import { UserSettings } from "@/lib/types/user";
import useUserSettings from "@/lib/queries/user/useUserSettings";
import { useStytchUser } from "@stytch/react";
import Loading from "./Loading";
import { AirplaneTakeoff, FloppyDisk } from "@phosphor-icons/react";
import { Switch } from "./ui/switch";
import { motion } from "motion/react";
import { LANGUAGES, DEFAULT_DISPLAY_LANGUAGE } from "@/lib/constants/languages";
import {
  getDisplayLanguageOptions,
  getResultLanguageOptionsWithSame,
} from "@/lib/utils/languageOptions";
import useUser from "@/lib/queries/user/useUser";
import { useTranslation } from "react-i18next";
import { SearchableSelect } from "./ui/combobox";
import { DeleteAccountSection } from "./profile/DeleteAccountSection";

const ProfileLabel = React.forwardRef<
  React.ElementRef<typeof FormLabel>,
  React.ComponentPropsWithoutRef<typeof FormLabel> & {
    htmlFor: string;
    children: React.ReactNode;
  }
>(({ htmlFor, children, ...props }, ref) => {
  return (
    <FormLabel
      ref={ref}
      htmlFor={htmlFor}
      className="flex flex-row items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
      {...props}
    >
      {children}
    </FormLabel>
  );
});
ProfileLabel.displayName = "ProfileLabel";

const ProfileInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentPropsWithoutRef<typeof Input> & { id: string; type: string }
>(({ id, type, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      className="transition-all duration-200 border-gray-300 rounded-lg bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 h-11"
      id={id}
      type={type}
      {...props}
    />
  );
});
ProfileInput.displayName = "ProfileInput";

function ProfileSettings({ data }: { data: UserSettings }) {
  const { t } = useTranslation(["settings", "common"]);
  const { useUpdateUser } = useUserSettings();
  const { mutate, isPending } = useUpdateUser();
  const { user: stytchUser, fromCache } = useStytchUser();
  const { data: user } = useUser({
    enabled: stytchUser !== null,
  });
  const hasActiveSubscription = user?.subscribed !== false;

  // Create validation schema with translations
  const settingsFormSchema = z
    .object({
      firstName: z.string(),
      middleName: z.string(),
      lastName: z.string(),
      school: z.string(),
      displayLanguage: z.string().optional(),
      resultLanguage: z.string().nullable().optional(),
      pilot: z.boolean().optional(),
      pilotEmail: z.string().optional(),
      preferences: z
        .object({
          darkMode: z.boolean().optional(),
          highContrast: z.boolean().optional(),
          lowAnimation: z.boolean().optional(),
          dyslexiaFont: z.boolean().optional(),
          largeText: z.boolean().optional(),
        })
        .optional(),
    })
    .refine(
      (data) => {
        // If pilot is enabled, pilotEmail must be a valid email
        if (data.pilot && (!data.pilotEmail || data.pilotEmail.trim() === "")) {
          return false;
        }
        // If pilot is enabled and pilotEmail is provided, it must be a valid email format
        if (data.pilot && data.pilotEmail && data.pilotEmail.trim() !== "") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(data.pilotEmail);
        }
        return true;
      },
      {
        message: t("pilot.validation.emailRequired"),
        path: ["pilotEmail"],
      }
    );
  const form = useForm<z.infer<typeof settingsFormSchema>>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: {
      firstName: data.firstName ?? "",
      middleName: data.middleName ?? "",
      lastName: data.lastName ?? "",
      school: data.school ?? "",
      displayLanguage:
        data.displayLanguage ?? data.language ?? DEFAULT_DISPLAY_LANGUAGE,
      resultLanguage: data.resultLanguage ?? null,
      pilot: data.pilot ?? false, // Provide fallback to false if not set
      pilotEmail: data.pilotEmail ?? "",
      preferences: {
        darkMode: data.preferences?.darkMode ?? false,
        highContrast: data.preferences?.highContrast ?? false,
        lowAnimation: data.preferences?.lowAnimation ?? false,
        dyslexiaFont: data.preferences?.dyslexiaFont ?? false,
        largeText: data.preferences?.largeText ?? false,
      },
    },
  });

  const pilotEnabled = form.watch("pilot");

  // // Auto-populate pilot email when pilot is enabled and stytch email exists
  // // Clear pilot email when pilot is disabled
  const handlePilotToggle = (checked: boolean) => {
    form.setValue("pilot", checked);
    if (checked && !form.getValues("pilotEmail")) {
      // Only auto-populate if the field is empty and pilot is being enabled
      if (stytchUser?.emails && stytchUser.emails.length > 0) {
        form.setValue("pilotEmail", stytchUser.emails[0].email);
      }
    } else if (!checked) {
      // Clear the pilot email when pilot is disabled
      form.setValue("pilotEmail", "");
    }
  };

  const onSubmit = async (formData: z.infer<typeof settingsFormSchema>) => {
    // Prepare data for submission
    const submitData = {
      ...formData,
      displayLanguage: formData.displayLanguage ?? DEFAULT_DISPLAY_LANGUAGE,
      resultLanguage: formData.resultLanguage,
      pilot: formData.pilot ?? false,
      // Set pilotEmail to empty string if pilot is disabled, otherwise use the form value
      pilotEmail: formData.pilot ? formData.pilotEmail || null : null,
      preferences: {
        darkMode: formData.preferences?.darkMode ?? false,
        highContrast: formData.preferences?.highContrast ?? false,
        lowAnimation: formData.preferences?.lowAnimation ?? false,
        dyslexiaFont: formData.preferences?.dyslexiaFont ?? false,
        largeText: formData.preferences?.largeText ?? false,
      },
    };
    mutate(submitData);
  };

  if (fromCache) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col w-full h-full min-h-screen px-6 pt-12 pb-8 overflow-auto">
      <div className="w-full max-w-4xl ">
        {/* <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">
            Profile Settings
          </h1>
          <p className="text-lg text-gray-600">
            Manage your account information and preferences
          </p>
        </div> */}

        <Card className="w-full border-0 shadow-xl bg-card/80 backdrop-blur-sm md:w-fit">
          <CardHeader
            className="pb-6 text-white rounded-t-lg bg-gradient-to-r from-blue-gradient to-primary dark:from-gray-50 dark:to-card"
            // style={{
            //   backgroundImage:
            //     "linear-gradient(to right, hsl(var(--blue-gradient)), hsl(var(--primary)), hsl(var(--primary)))",
            // }}
          >
            <CardTitle className="inline-block text-2xl font-bold dark:text-card-foreground">
              {t("profile.title")}
            </CardTitle>
            <CardDescription className="text-white/80 dark:text-card-foreground">
              {t("profile.description")}
            </CardDescription>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent className="p-8 space-y-8">
                {/* Email Section */}
                <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                        />
                      </svg>
                    </div>
                    <ProfileLabel htmlFor="email">
                      {t("email.label")}
                    </ProfileLabel>
                  </div>
                  <div className="flex flex-row items-center justify-between gap-4">
                    {stytchUser?.emails && stytchUser?.emails.length > 0 && (
                      <Input
                        className="font-medium text-gray-700 bg-white border-gray-300"
                        readOnly
                        name="email"
                        value={stytchUser?.emails[0]?.email}
                      />
                    )}
                    <Link
                      to={SETTINGS_CHANGE_PASSWORD}
                      className="px-4 py-2 text-sm font-medium transition-colors rounded-lg text-primary hover:text-primary/80 hover:underline whitespace-nowrap bg-primary/10"
                    >
                      {t("email.changePassword")}
                    </Link>
                  </div>
                </div>

                {/* Name Fields Section */}
                <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("personalDetails.title")}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <FormField
                      name="firstName"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <ProfileLabel htmlFor="firstName">
                            {t("personalDetails.firstName")}
                          </ProfileLabel>
                          <FormControl>
                            <ProfileInput
                              id="firstName"
                              type="text"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage>
                            {form.formState.errors.firstName?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="middleName"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <ProfileLabel htmlFor="middleName">
                            {t("personalDetails.middleName")}
                          </ProfileLabel>
                          <FormControl>
                            <ProfileInput
                              id="middleName"
                              type="text"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage>
                            {form.formState.errors.middleName?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="lastName"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <ProfileLabel htmlFor="lastName">
                            {t("personalDetails.lastName")}
                          </ProfileLabel>
                          <FormControl>
                            <ProfileInput
                              id="lastName"
                              type="text"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage>
                            {form.formState.errors.lastName?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* School Section */}
                <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("institution.title")}
                    </h3>
                  </div>
                  <FormField
                    name="school"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <ProfileLabel htmlFor="school">
                          {t("institution.label")}
                        </ProfileLabel>
                        <FormControl>
                          <ProfileInput id="school" type="text" {...field} />
                        </FormControl>
                        <FormMessage>
                          {form.formState.errors.school?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>

                {/* Language Preferences Section */}
                <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("language.title")}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    {/* Display Language */}
                    <FormField
                      name="displayLanguage"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <ProfileLabel htmlFor="displayLanguage">
                            {t("language.display.label", "Interface Language")}
                          </ProfileLabel>
                          <FormControl>
                            <SearchableSelect
                              options={getDisplayLanguageOptions()}
                              onValueChange={field.onChange}
                              value={field.value || DEFAULT_DISPLAY_LANGUAGE}
                              placeholder={t(
                                "language.placeholder",
                                "Select a language..."
                              )}
                              searchPlaceholder={t(
                                "searchLanguage",
                                "Search languages..."
                              )}
                              emptyText={t(
                                "noLanguageFound",
                                "No language found."
                              )}
                            />
                          </FormControl>
                          {(form.watch("displayLanguage") === LANGUAGES.OTHER ||
                            !Object.values(LANGUAGES).includes(
                              form.watch("displayLanguage") as LANGUAGES
                            )) && (
                            <Input
                              className="mt-2 transition-all duration-200 bg-white border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 h-11"
                              placeholder={t("language.other.placeholder")}
                              value={
                                form.watch("displayLanguage") ===
                                LANGUAGES.OTHER
                                  ? ""
                                  : form.watch("displayLanguage")
                              }
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          )}
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "language.display.description",
                              "The language used for the interface and menus."
                            )}
                          </p>
                          <FormMessage>
                            {form.formState.errors.displayLanguage?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />

                    {/* Result Language */}
                    <FormField
                      name="resultLanguage"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <ProfileLabel htmlFor="resultLanguage">
                            {t(
                              "language.result.label",
                              "Generated Content Language"
                            )}
                          </ProfileLabel>
                          <FormControl>
                            <SearchableSelect
                              options={getResultLanguageOptionsWithSame(
                                t(
                                  "language.result.sameAsDisplay",
                                  "Same as interface language"
                                )
                              )}
                              onValueChange={(value) =>
                                field.onChange(value === "same" ? null : value)
                              }
                              value={field.value || "same"}
                              placeholder={t(
                                "language.result.placeholder",
                                "Select content language..."
                              )}
                              searchPlaceholder={t(
                                "searchLanguage",
                                "Search languages..."
                              )}
                              emptyText={t(
                                "noLanguageFound",
                                "No language found."
                              )}
                            />
                          </FormControl>
                          {form.watch("resultLanguage") === LANGUAGES.OTHER && (
                            <Input
                              className="mt-2 transition-all duration-200 bg-white border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 h-11"
                              placeholder={t("language.other.placeholder")}
                              value={
                                field.value === LANGUAGES.OTHER
                                  ? ""
                                  : field.value || ""
                              }
                              onChange={(e) => field.onChange(e.target.value)}
                            />
                          )}
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "language.result.description",
                              "The default language for generated content like lesson plans and communications."
                            )}
                          </p>
                          <FormMessage>
                            {form.formState.errors.resultLanguage?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Preferences Section */}
                <div className="p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("preferences.title")}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <FormField
                      name="preferences.darkMode"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between p-4 space-y-0 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="space-y-0.5">
                            <ProfileLabel htmlFor="darkMode">
                              {t("preferences.darkMode.label")}
                            </ProfileLabel>
                            <p className="text-sm text-muted-foreground">
                              {t("preferences.darkMode.description")}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              id="darkMode"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="preferences.highContrast"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between p-4 space-y-0 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="space-y-0.5">
                            <ProfileLabel htmlFor="highContrast">
                              {t("preferences.highContrast.label")}
                            </ProfileLabel>
                            <p className="text-sm text-muted-foreground">
                              {t("preferences.highContrast.description")}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              id="highContrast"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="preferences.lowAnimation"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between p-4 space-y-0 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="space-y-0.5">
                            <ProfileLabel htmlFor="lowAnimation">
                              {t("preferences.lowAnimation.label")}
                            </ProfileLabel>
                            <p className="text-sm text-muted-foreground">
                              {t("preferences.lowAnimation.description")}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              id="lowAnimation"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="preferences.dyslexiaFont"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between p-4 space-y-0 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="space-y-0.5">
                            <ProfileLabel htmlFor="dyslexiaFont">
                              {t("preferences.dyslexiaFont.label")}
                            </ProfileLabel>
                            <p className="text-sm text-muted-foreground">
                              {t("preferences.dyslexiaFont.description")}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              id="dyslexiaFont"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="preferences.largeText"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between p-4 space-y-0 border border-gray-200 rounded-lg bg-gray-50 md:col-span-2">
                          <div className="space-y-0.5">
                            <ProfileLabel htmlFor="largeText">
                              {t("preferences.largeText.label")}
                            </ProfileLabel>
                            <p className="text-sm text-muted-foreground">
                              {t("preferences.largeText.description")}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              id="largeText"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Pilot Program Section */}
                {user?.subscribed && (
                  <div className="p-3 mt-4 text-yellow-800 border-2 border-yellow-200 rounded-lg bg-yellow-50 dark:bg-yellow-800 dark:border-yellow-700 dark:text-yellow-50">
                    <div className="flex items-center gap-2 mb-2">
                      <AirplaneTakeoff className="w-5 h-5 " />
                      <h3 className="text-lg font-semibold ">
                        {t("pilot.title")}
                      </h3>
                    </div>
                    <p
                      className="mb-3 text-sm"
                      dangerouslySetInnerHTML={{
                        __html: t("pilot.description"),
                      }}
                    ></p>

                    <FormField
                      name="pilot"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between mb-3 space-y-0">
                          <div className="space-y-0.5">
                            <ProfileLabel htmlFor="pilot">
                              {t("pilot.enable.label")}
                            </ProfileLabel>
                            <p className="text-sm text-muted-foreground">
                              {t("pilot.enable.description")}
                            </p>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                handlePilotToggle(checked);
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {pilotEnabled && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{
                          duration: 0.3,
                          ease: "easeInOut",
                          height: { duration: 0.4 },
                        }}
                        style={{ overflow: "hidden" }}
                      >
                        <FormField
                          name="pilotEmail"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <ProfileLabel htmlFor="pilotEmail">
                                {t("pilot.email.label")}
                                {pilotEnabled && (
                                  <span className="px-2 py-1 text-sm rounded text-destructive-foreground bg-destructive">
                                    {t("pilot.email.required")}
                                  </span>
                                )}
                              </ProfileLabel>

                              <FormControl>
                                <ProfileInput
                                  id="pilotEmail"
                                  type="email"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage className="text-sm text-light-grey dark:text-yellow-50">
                                {pilotEnabled
                                  ? t("pilot.email.description")
                                  : t("pilot.email.disabled")}
                              </FormMessage>
                              <FormMessage>
                                {form.formState.errors.pilotEmail?.message}
                              </FormMessage>
                            </FormItem>
                          )}
                        />
                      </motion.div>
                    )}
                  </div>
                )}

                <DeleteAccountSection
                  className="mt-10"
                  hasActiveSubscription={hasActiveSubscription}
                />
              </CardContent>
              <CardFooter className="px-8 py-6 border-t border-gray-200 rounded-b-lg bg-gray-50">
                <div className="flex flex-row items-center justify-end w-full">
                  <Button
                    type="submit"
                    isLoading={isPending}
                    loadText={t("actions.saving")}
                    className="h-12 px-8 font-semibold transition-all duration-200 transform rounded-lg shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-xl hover:scale-105"
                  >
                    <FloppyDisk className="w-5 h-5 mr-2" />
                    {t("actions.saveChanges")}
                  </Button>
                </div>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default ProfileSettings;
